import type { SlideMenuItem } from "@lxc/app-device-common";
import type LxcError from "~/core/models/LxcError.class";

export type FilterSelectionValue = string | string[] | number | number[];

export type filterOperator =
  | "="
  | "<="
  | ">="
  | "=="
  | "=like="
  | "=ilike="
  | "=in="
  | "=out="
  | "=arct="
  | "=arctjb="
  | "in=fleet="
  | "out=fleet="
  | "not::"
  | "=notnull="
  | "=anymatch="
  | "=allmatch=";

export const OPERATOR_EQUAL: filterOperator = "=";
export const OPERATOR_LOWER_OR_EQUAL: filterOperator = "<=";
export const OPERATOR_GREATER_OR_EQUAL: filterOperator = ">=";
export const OPERATOR_RSQL_EQUAL: filterOperator = "==";
export const OPERATOR_RSQL_ARRAY_CONTAINS: filterOperator = "=arct=";
export const OPERATOR_RSQL_ARRAY_CONTAINS_JSONB: filterOperator = "=arctjb=";
export const OPERATOR_RSQL_LIKE: filterOperator = "=like=";
export const OPERATOR_RSQL_ILIKE: filterOperator = "=ilike=";
export const OPERATOR_RSQL_IN: filterOperator = "=in=";
export const OPERATOR_RSQL_OUT: filterOperator = "=out=";
export const OPERATOR_RSQL_NEGATE: filterOperator = "not::";
export const CUSTOM_IN_FLEET_RSQL_OPERATOR: filterOperator = "in=fleet=";
export const CUSTOM_OUT_FLEET_RSQL_OPERATOR: filterOperator = "out=fleet=";
export const OPERATOR_RSQL_NOT_NULL: filterOperator = "=notnull=";
export const OPERATOR_RSQL_ANY_MATCH: filterOperator = "=anymatch=";
export const OPERATOR_RSQL_ALL_MATCH: filterOperator = "=allmatch=";

export interface FilterSelectionDefinition {
  key: string | string[];
  operator: filterOperator;
  value: FilterSelectionValue;
  fallbackValue?: FilterSelectionValue;
  valueFormatter?: (value: FilterSelectionValue) => FilterSelectionValue;
}

export type FiltersSelection = Map<Filters, FilterSelectionDefinition>;

export interface Option {
  label: string;
  value: string | string[];
  disabled?: boolean;
}

export interface GenericOption {
  id: string;
  label: string;
  [key: string]: unknown;
}

export type FilterOption = Option | GenericOption;

export interface FilterOptions {
  label?: string;
  options: Option[];
}

export enum Filters {
  APPLICATION_NAME = "applicationName",
  APPMGT_DATE_ADDED_AFTER = "appmgtDateAddedAfter",
  APPMGT_DATE_ADDED_BEFORE = "appmgtDateAddedBefore",
  APPMGT_DATE_UPDATED_AFTER = "appmgtDateUpdatedAfter",
  APPMGT_DATE_UPDATED_BEFORE = "appmgtDateUpdatedBefore",
  APPMGT_DEVICE_TYPES = "appmgtDeviceTypes",
  BUSINESS_UNIT_NAME = "businessUnitName",
  CAMPAIGN_TYPE = "campaignType",
  CAMPAIGN_STATUS = "campaignStatus",
  CERTIFICATE_EXPIRE_AFTER = "certificateExpireAfter",
  CERTIFICATE_EXPIRE_BEFORE = "certificateExpireBefore",
  CONNECTIVITY = "connectivityState",
  CONTENT_TAGS = "contentTags",
  DATE_ADDED_AFTER = "dateAddedAfter",
  DATE_ADDED_BEFORE = "dateAddedBefore",
  DEVICE_FIRMWARE_VERSIONS = "deviceFirmwareVersions",
  DEVICE_FIRMWARE_VERSION_INCLUDED_OR_NULL = "firmwareVersionIncludedOrNull",
  DEVICE_HARDWARE_VERSIONS = "deviceHardwareVersions",
  DTWINS_CONFIGURATION_VERSIONS = "deviceTwinConfigurationVersion",
  DTWINS_IN_FLEET = "deviceTwinsInFleet",
  DTWINS_IN_LIFE_CYCLE_STATE = "dtwinInLifeCycleState",
  DTWINS_NOT_IN_FLEET = "deviceTwinsNotInFleet",
  DTWIN_CONFIGURATION_END_DATE = "deviceTwinsConfigurationEndDate",
  DTWIN_CONFIGURATION_START_DATE = "deviceTwinsConfigurationStartDate",
  DTWIN_CONFIGURATION_VERSION_NAME_OR_VERSION = "deviceTwinsConfigurationVersionName",
  DTWIN_CONNECTIVITY_STATUS = "deviceTwinsConnectivityStatus",
  DTWIN_HARDWARE_VERSION_NOT_NULL = "deviceTwinsHardwareVersionNotNull",
  DTWIN_IN_HARDWARE_VERSION = "deviceTwinsInHardwareVersion",
  DTWIN_IN_SOFTWARE_VERSION = "deviceTwinsInSoftwareVersion",
  DTWIN_LATITUDE_NOT_NULL = "deviceTwinLatitudeNotNull",
  DTWIN_LONGITUDE_NOT_NULL = "deviceTwinLongitudeNotNull",
  DTWIN_MODEL_TYPE = "deviceTwinModelType",
  DTWIN_NAME_OR_SERIAL_NUMBER = "deviceTwinNameOrSerialNumber",
  DTWIN_OPERATION_MODEL_DEVICE_TYPE = "deviceTwinOperationModelDeviceType",
  DTWIN_OPERATION_MODEL_HIDDEN = "deviceTwinOperationModelHidden",
  DTWIN_OPERATION_MODEL_TYPE = "deviceTwinOperationModelType",
  DTWIN_CREATED_IN_CAMPAIGN = "createdInCampaign",
  DTWIN_OPERATION_TYPE = "deviceTwinOperationType",
  DTWIN_OPERATION_UID = "deviceTwinOperationUid",
  DTWIN_RANGE = "attributes.range",
  DTWIN_UID = "deviceTwinUid",
  DTWIN_UID_OUT = "deviceTwinUidOut",
  FIRMWARE_NAME = "firmwareName",
  FIRMWARE_NAME_OR_VERSION = "firmwareNameOrVersion",
  FIRMWARE_VERSIONS = "firmwareVersions",
  FLEET_NAME = "fleetName",
  GLOBAL_CONFIGURATION_ELIGIBILITY = "globalConfigurationEligibility",
  HARDWARE_VERSIONS = "hardwareVersions",
  KEYSTORE_ALIAS = "keystoreAlias",
  KEYSTORE_TAGS = "keystoreTags",
  LABEL = "label",
  LOG_ACTION = "action",
  LOG_ACTION_TYPE = "actionType",
  LOG_COMPONENT_ID = "componentId",
  LOG_END_DATE = "endDate",
  LOG_AUTHOR_CLASS = "authorClass",
  LOG_AUTHOR_ID = "authorId",
  LOG_EVENT_TYPE = "eventType",
  LOG_LEVEL = "level",
  LOG_ORIGIN = "origin",
  LOG_START_DATE = "startDate",
  LOG_TYPE = "type",
  MODEL_DECLINATION = "lxcModelDeclination",
  MODEL_TYPE = "lxcModelType",
  NAME = "name",
  OPERATION_CAMPAIGN_UID = "operationCampaignUid",
  ORGANIZATION_NAME = "organizationName",
  PKI_ALIAS = "pkiAlias",
  PKI_TRUST_CHAIN_ALIAS = "pkiTrustChainAlias",
  RANGE = "range",
  RANGE_FRIENDLY_NAME = "rangeFriendlyName",
  SECTORS = "sectors",
  SECTORS_EXCLUDED = "sectorsExclude",
  SORT_BY = "sortBy",
  SORT_DIRECTION = "sortDirections",
  STATE = "state",
  STATUS = "status",
  TRUSTSTORE_ALIAS = "truststoreAlias",
  TRUSTSTORE_TAGS = "truststoreTags",
  USER_FIRST_OR_LAST_NAME = "userFirstOrLastName",
}

export enum FiltersType {
  FILTERS_SELECTION,
  MICROSOFT_FORMAT,
  PIPE_SEPARATOR,
  RAW,
  /**
   * The filter uses RSQL syntax described here
   * @see <a href="https://gitlab.tools.lx-connect.com/lxconnect/lx-iot-backend/lxc-core/lxc-bs-starters/lxc-rsql/-/tree/main/lxc-rsql-sql?ref_type=heads">Gitlab lxc-rsql</a>
   */
  RSQL,
  TWO_AMPERSAND_SEPARATOR,
}

export enum FilterInputType {
  RADIO,
  CHECKBOX,
  PERIOD_PICKER,
  CUSTOM,
}

export interface FilterFormSection extends SlideMenuItem {
  additionalFilter?: Filters; // Filter for the end date for example
  emptyOptionsText?: string;
  /**
   * The current filter name
   * Should correspond to the one declared in filters in FiltersComponent
   */
  filter: Filters;
  /**
   * Represents the type of input used for a filter.
   *
   * With FilterInputType.CUSTOM, you must define the component
   * in a slot and handle events of filtersComponent has props
   * selectedFilters, selectedTags, filtersByType and events has
   * appliedFiltersChange, reset, deleteSelected
   */
  inputType: FilterInputType;
  loading?: Ref<boolean>;
  loadingError?: Ref<LxcError | undefined | null>;
  options?: Ref<FilterOption[]>; // the object must have an id and label property
  /**
   * The tag prefix to show selected filters
   */
  tagPrefix: string;
  translate?: boolean; // if the translation is to be done in the LxcFilter component and already provided in the LxcOptions. It can be necessary to set to false if the options list are readonly (Computed).
}
